import * as React from 'react'
import type { Params } from 'react-router-dom'
import { Outlet, useParams } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { makeObservable, observable } from 'mobx'
import moment from 'moment'

import { observer } from 'mobx-react'

import { Employee, useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import ClockIn from 'modules/attendance/components/ClockIn'
import ClockInStore from 'modules/attendance/components/ClockIn/Store'
import { Layout } from 'modules/attendance/components/ClockIn/types'

import { Cycle, useAttendanceDateNavigator } from '../lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from '../lib/attendanceFeaturesContext'
import { AttendancePoliciesProvider, useAttendancePolicies } from '../lib/attendancePolicies'
import { ContractsVersionProvider } from '../lib/contractsVersionContext'

interface State {
  loading: boolean
}
interface Props {
  params: Params
  employeeId: number
  projectsFeatureAvailability: boolean
  dateFrom: Date
  dateTo: Date
  cycle: Cycle
  canReadOwnedAttendance: boolean
}

const PeriodContainer = observer(
  class PeriodContainer extends React.Component<Props, State> {
    state = {
      loading: true,
    }

    store: ClockInStore

    constructor(props: Props) {
      super(props)
      makeObservable(this, {
        store: observable,
      })
      this.store = this._getNewStore()
    }

    componentDidMount() {
      this.fetch()
    }

    _getNewStore(): ClockInStore {
      const {
        params: { year, month },
        employeeId,
        projectsFeatureAvailability,
      } = this.props

      const coercedProps = {
        year: Number(year),
        month: Number(month),
        employeeId,
        currentEmployeeId: employeeId,
        layout: Layout.employee,
        hasNewProjectsSection: projectsFeatureAvailability,
      }

      return new ClockInStore(coercedProps)
    }

    componentDidUpdate(prevProps: Props) {
      const {
        params: { year, month },
        cycle,
        dateFrom,
      } = this.props

      const coercedParams = {
        year: Number(year),
        month: Number(month),
      }

      const isMonthChanged = this.store.monthChanged(coercedParams)
      const isWeekChanged = cycle === Cycle.weekly && prevProps.dateFrom !== dateFrom
      const isCycleChanged = cycle !== prevProps.cycle

      if (isMonthChanged || isWeekChanged || isCycleChanged) {
        this.store = this._getNewStore()
        this.fetch()
      }
    }

    async fetch() {
      const { dateFrom, dateTo } = this.props
      this.setState({
        loading: true,
      })

      await this.store.fetch(moment(dateFrom), moment(dateTo))
      this.setState({
        loading: false,
      })
    }

    render() {
      const { loading } = this.state

      return (
        <ClockIn
          loading={loading}
          store={this.store}
          canReadOwnedAttendance={this.props.canReadOwnedAttendance}
        >
          <Outlet />
        </ClockIn>
      )
    }
  }
)

const PeriodContainerWrapper = ({ employee }: { employee: Employee }) => {
  const params = useParams()
  const { featureAvailability: projectsFeature } = useAttendanceFeatures(
    Features.projects_management
  )
  const { dateFrom, dateTo, cycle } = useAttendanceDateNavigator()
  const { data } = useAttendancePolicies()

  const canReadOwnedAttendance = !!data?.employee?._policies?.seeAttendance.can

  return (
    <PeriodContainer
      employeeId={Number(employee?.id)}
      params={params}
      projectsFeatureAvailability={projectsFeature}
      dateFrom={dateFrom}
      dateTo={dateTo}
      cycle={cycle}
      canReadOwnedAttendance={canReadOwnedAttendance}
    />
  )
}

const EmployeePeriodRootPage = () => {
  const { employee } = useCurrentSession()

  return (
    <ContractsVersionProvider employeeId={employee?.id ?? 0}>
      <AttendancePoliciesProvider employeeId={employee?.id ?? 0}>
        <PeriodContainerWrapper employee={employee} />
      </AttendancePoliciesProvider>
    </ContractsVersionProvider>
  )
}

export default EmployeePeriodRootPage
